import { ApolloLink, Operation, FetchResult, Observable } from '@apollo/client/core';

import { print } from 'graphql';
import { createClient, ClientOptions, Client } from 'graphql-ws';
import { Store } from 'vuex';
import { LeaseManager } from '@/license/LeaseManager';

/**
 * Custom WebSocket implementation for graphql-ws to support msw.
 * It returns an instance of the globalThis.WebSocket in the constructor,
 *
 * When graphql-ws createClient is called, it stores the WebSocket implementation it will
 * use in a variable. If msw hasn't patched the WebSocket class at this point,
 * graphql-ws will use the original WebSocket and no interception will happen.
 */
class LazyWebSocket {
  static CLOSED = globalThis.WebSocket.CLOSED;
  static CLOSING = globalThis.WebSocket.CLOSING;
  static CONNECTING = globalThis.WebSocket.CONNECTING;
  static OPEN = globalThis.WebSocket.OPEN;

  constructor(...args: [url: string | URL, protocols?: string | Array<string>]) {
    return new globalThis.WebSocket(...args);
  }
}

export class WebSocketLink extends ApolloLink {
  private client: Client;

  constructor(
    options: ClientOptions,
    {
      getStore,
      getLeaseManager,
      getAuthInstance,
    }: {
      // these are functions because they might not be instantiated when the link is created
      getStore: () => Store<any>;
      getLeaseManager: (store: Store<any>) => LeaseManager;
      getAuthInstance: () => any;
    }
  ) {
    super();

    this.client = createClient({
      ...options,
      webSocketImpl: LazyWebSocket,
      connectionParams: async () => {
        // Request the auth0 access token and lease token when the client first
        // connects to the server. The tokens are validated by the server whenever a subscription
        // is added.

        const [authToken, leaseToken, idToken] = await Promise.all([
          getAuthInstance().getTokenSilently(),
          getLeaseManager(getStore()).requestLeaseToken(),
          getAuthInstance().getIdTokenClaims(),
        ]);

        return {
          authToken,
          leaseToken,
          idToken: idToken?.__raw,
        };
      },
    });

    this.client.on('connected', () => {
      getStore().commit('user/USER_SET_SUBSCRIPTIONS_AVAILABILITY', true);
    });

    this.client.on('closed', () => {
      getStore().commit('user/USER_SET_SUBSCRIPTIONS_AVAILABILITY', false);
    });
  }

  public request(operation: Operation): Observable<FetchResult> {
    return new Observable((sink) =>
      this.client.subscribe<FetchResult>(
        {
          ...operation,
          query: print(operation.query),
        },
        {
          next: sink.next.bind(sink),
          complete: sink.complete.bind(sink),
          error: sink.error.bind(sink),
        }
      )
    );
  }
}
